import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { COMPANY_FAQ_CATEGORIES } from "@/content/company-corpus/faq-defs";

export const Route = createFileRoute("/company/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/company-corpus");
    return { faqs: m.COMPANY_FAQ_TH };
  },
  component: CompanyFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `จดทะเบียนบริษัทต่างชาติในไทย — FAQ ${faqs.length} ข้อ อ้างอิงราชการ`,
        description:
          "คำถามที่พบบ่อยเรื่องจดทะเบียนบริษัทในไทยสำหรับผู้ถือหุ้นต่างชาติ — สัดส่วนหุ้น 49% ความเสี่ยงนอมินี ใบอนุญาตประกอบธุรกิจคนต่างด้าว Treaty of Amity สิทธิประโยชน์ BOI ทุนจดทะเบียนกับวีซ่า ภาษี VAT และการยื่นงบการเงิน อ้างอิง dbd.go.th, boi.go.th และ rd.go.th",
        path: "/company/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/company/faq" }],
        keywords: [
          "จดทะเบียนบริษัท ต่างชาติ",
          "ใบอนุญาตประกอบธุรกิจคนต่างด้าว",
          "Treaty of Amity",
          "BOI",
          "นอมินี ผิดกฎหมาย",
          "ทุนจดทะเบียน วีซ่าทำงาน",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "จดทะเบียนบริษัท FAQ", url: `${site}/company/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/company/faq")) },
      ],
    };
  },
});

function CompanyFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>จดทะเบียนบริษัท FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          จดทะเบียนบริษัทและใบอนุญาตประกอบธุรกิจสำหรับคนต่างชาติ — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground">
          คำตอบอ้างอิงประมวลกฎหมายแพ่งและพาณิชย์ พระราชบัญญัติการประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542
          ประกาศของกรมพัฒนาธุรกิจการค้า (dbd.go.th) สำนักงานคณะกรรมการส่งเสริมการลงทุน (boi.go.th)
          กรมสรรพากร (rd.go.th) และกรมการจัดหางาน (doe.go.th)
        </p>
        <div className="mb-8 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">ข้อควรทราบ:</strong> การถือหุ้นผ่านผู้ถือหุ้นไทยที่ไม่ได้ลงทุนจริง
          (นอมินี) เป็นความผิดตามพระราชบัญญัติการประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 เราไม่ให้บริการโครงสร้างนอมินี
          การอนุมัติใบอนุญาตและสิทธิประโยชน์ทุกกรณีเป็นดุลพินิจของหน่วยงาน ค่าธรรมเนียมและกรอบเวลาอาจเปลี่ยนแปลง
          โปรดตรวจสอบกับหน่วยงานก่อนดำเนินการ
        </div>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/company/faq" className="text-primary underline">
            Company registration in Thailand FAQ →
          </a>
        </div>

        <section aria-label="หมวดคำถามจดทะเบียนบริษัท" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกดูตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {COMPANY_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/company/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm"
              >
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">บริการที่เกี่ยวข้อง</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/work-permit/type">ใบอนุญาตทำงานคนต่างชาติ</a></li>
            <li><a className="text-primary underline" href="/visa/thai/non-b">วีซ่าธุรกิจ Non-B</a></li>
            <li><a className="text-primary underline" href="/translation/faq">แปลและรับรองเอกสารบริษัท</a></li>
            <li><a className="text-primary underline" href="/notary/faq">รับรองลายมือชื่อและเอกสารเพื่อใช้ต่างประเทศ</a></li>
          </ul>
        </section>

        <section className="mt-12 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">ยังหาคำตอบไม่เจอ?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            สอบถามโครงสร้างผู้ถือหุ้น เอกสารที่ต้องใช้ และขอบเขตงานของกรณีคุณกับทีมงานได้ทางโทรศัพท์ LINE
            หรืออีเมล — จ.-ส. 9:00-18:00
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">
            ติดต่อทีมงาน →
          </a>
        </section>

        <FaqSourceNote topic="company" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}